<script setup lang="ts">
import type { HTMLAttributes, TextareaHTMLAttributes } from 'vue';
import type { ControlSize, ControlWidth } from '@/components/base/_internal/control';
import { useVModel } from '@vueuse/core';
import { computed } from 'vue';
import { controlWidthClass, useControlSize, useControlWidth, useFieldControl } from '@/components/base/_internal/control';
import { cn } from '@/utils/shadcn/lib/utils.ts';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<{
    class?: HTMLAttributes['class']
    name?: TextareaHTMLAttributes['name']
    defaultValue?: string | number
    modelValue?: string | number
    /** Falls back to the size a container provides (see `SphControlProvider`), then to `md`. */
    size?: ControlSize
    /** Falls back to the width a container provides, then fills the container. `auto` fills as well. */
    width?: ControlWidth
    id?: string
    resizable?: boolean
    required?: boolean
    invalid?: boolean
    disabled?: boolean
}>(), {
    size: undefined,
    width: undefined,
    id: undefined,
    resizable: false,
});

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | number): void
}>();

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
});

const size = useControlSize(() => props.size, 'md');
const width = useControlWidth(() => props.width, 'text');
// A textarea grows with its content, so a natural width would change with every line typed.
const effectiveWidth = computed(() => (width.value === 'auto' ? 'full' : width.value));
const field = useFieldControl(() => props, () => effectiveWidth.value);
</script>

<template>
    <textarea
        v-model="modelValue"
        :name="props.name"
        :id="field.id"
        :aria-describedby="field.describedBy"
        :required="field.required"
        :aria-invalid="field.invalid ? 'true' : 'false'"
        :disabled="field.disabled"
        data-slot="textarea"
        :data-size="size"
        class="sph-form-control"
        :class="cn('border-input-dark text-input-foreground placeholder:text-muted-foreground flex field-sizing-content min-h-16 w-full rounded-md border bg-input px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none data-[size=sm]:px-2 data-[size=sm]:py-1.5 data-[size=sm]:text-sm', controlWidthClass(effectiveWidth), props.class, props.resizable ? 'resize-y' : 'resize-none')"
    ></textarea>
</template>
